/**
 * DiffuserModulePage.tsx — Diffuser Module Engineering Design
 *
 * Each module is a 2 m × 2 m submerged HDPE frame holding 4, 6, 8, 10 or 12
 * membrane disc diffusers at adjustable spacing. The page provides:
 *
 *   · SVG plan-view illustration of the module frame + diffuser positions
 *   · Per-module air release, bubble formation & rise physics
 *     (temperature-dependent — identical physics to ReservoirAerationSpecPage)
 *   · Module performance: SOTR, AOTR, O₂ per module per hour
 *   · Reservoir coverage assessment:
 *       – Input reservoir area and target coverage zone
 *       – Calculates modules required, total airflow, blower duty
 *       – Sediment control efficacy: areal O₂ coverage vs SOD
 *   · All inputs persist in sessionStorage so data survives page navigation
 *
 * Frame dimensions: fixed 2.0 m × 2.0 m (industry standard module size).
 * Diffuser spacing: automatically calculated from frame size and count,
 *   user can adjust within 0.2–1.8 m range.
 * Mounting: frame rests on legs 0.4 m above reservoir bed (adjustable).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  Wind, ArrowLeft, Info, CheckCircle2, AlertTriangle,
  RotateCcw, Layers, Activity, Database, BarChart3,
  ChevronDown, ChevronUp, Circle, Ruler, Gauge, Thermometer, FileText, Grid3x3, Sparkles,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { savePersisted, loadPersisted, KEYS } from '@/lib/sessionPersistence';

// ─── Session storage key ──────────────────────────────────────────────────────

const SESSION_KEY = 'rb_diffuser_module_v1';

// ─── Physics (identical to ReservoirAerationSpecPage) ────────────────────────

const G           = 9.81;
const P_ATM       = 101_325;
const RHO_AIR_STD = 1.293;
const O2_MASS_FRAC= 0.2314;
const GAMMA_AIR   = 1.4;
const DO_SAT_20   = 9.09;

function waterDensity(T: number): number {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T*T
       + 1.001685e-4*T*T*T - 1.120083e-6*T*T*T*T;
}
function waterViscosity(T: number): number {
  return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15));
}
function waterSurfaceTension(T: number): number {
  return Math.max(0.04, 0.07564 - 1.376e-4 * T);
}
function waterKinVisc(T: number): number {
  return waterViscosity(T) / waterDensity(T);
}
function doSat(T: number): number {
  return 468 / (31.6 + T);
}

/** Bubble diameter at orifice (mm) — Fritz (1935) + dynamic correction */
function bubbleDiamMm(poreSizeUm: number, airflowPerDiffNm3h: number, activePores: number, T: number): number {
  const d_pore  = poreSizeUm / 1e6;
  const sigma   = waterSurfaceTension(T);
  const rho_w   = waterDensity(T);
  const d_stat  = Math.pow((6 * sigma * d_pore) / (rho_w * G), 1 / 3);
  const q_pore  = (airflowPerDiffNm3h / 3600) / Math.max(1, activePores);
  const corr    = 1 + 0.9 * Math.pow(Math.max(q_pore * 1e9, 0.001), 0.25);
  return d_stat * corr * 1000;
}

/** Rise velocity with temperature correction (contaminated water, intermediate regime) */
function riseVelocityMs(d_mm: number, T: number): number {
  let v0: number;
  if (d_mm <= 0.5)      v0 = 0.13 + 0.16 * d_mm;
  else if (d_mm <= 3.0) v0 = 0.14 + 0.065 * d_mm;
  else if (d_mm <= 6.0) v0 = 0.335 + 0.02 * (d_mm - 3);
  else                   v0 = 0.395;
  const nu20 = waterKinVisc(20);
  return v0 * Math.pow(nu20 / waterKinVisc(T), 0.7);
}

/** Bubble diameter at surface — ideal gas pressure expansion */
function bubbleSurface(d_mm: number, depthM: number, T: number): number {
  const P_bed = P_ATM + waterDensity(T) * G * depthM;
  return d_mm * Math.pow(P_bed / P_ATM, 1 / 3);
}

/** SOTR (kg O₂/h) */
function calcSOTR(nm3h: number, soteDecimal: number): number {
  return nm3h * RHO_AIR_STD * O2_MASS_FRAC * soteDecimal;
}

/** AOTR (kg O₂/h) field-corrected */
function calcAOTR(sotr: number, alpha: number, beta: number, T: number, doMgL: number): number {
  const cs = doSat(T);
  const theta = Math.pow(1.024, T - 20);
  return sotr * alpha * beta * theta * Math.max(0, cs - doMgL) / DO_SAT_20;
}

/** Blower power (kW) — adiabatic */
function blowerKw(nm3h: number, submM: number, diffDropKpa: number, safetyPct: number, eff: number): number {
  const Q = nm3h / 3600;
  const P_in  = P_ATM;
  const P_out = P_in + (waterDensity(20) * G * submM + diffDropKpa * 1000) * (1 + safetyPct / 100);
  const exp   = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_in * Q * (Math.pow(P_out / P_in, exp) - 1) / eff / 1000;
}

// ─── Diffuser layout geometry ─────────────────────────────────────────────────

const FRAME_SIZE = 2.0; // m — fixed 2×2 m module

type DiffQty = 4 | 6 | 8 | 10 | 12;

interface DiffPosition { x: number; y: number }

/** Clamp corner offset to keep diffusers safely inside the frame */
function clampCornerOffset(c: number): number {
  return Math.min(Math.max(0.10, c), FRAME_SIZE * 0.40); // 0.10–0.80 m
}

/**
 * Returns diffuser positions (m, in frame coords 0–FRAME_SIZE).
 * cornerOffset = distance from frame edge to nearest diffuser centre (m).
 * All layouts anchor corner diffusers at (c,c), (L-c,c), (c,L-c), (L-c,L-c),
 * then add mid-edge or inner diffusers derived from the same offset.
 */
function diffuserLayout(count: DiffQty, cornerOffset: number): DiffPosition[] {
  const L   = FRAME_SIZE;
  const c   = clampCornerOffset(cornerOffset);
  const mid = L / 2;

  switch (count) {
    case 4:
      // 2×2 — four corners
      return [
        { x: c,   y: c   }, { x: L-c, y: c   },
        { x: c,   y: L-c }, { x: L-c, y: L-c },
      ];
    case 6:
      // 2×3 — two columns (c, L-c), three rows (c, mid, L-c)
      return [
        { x: c,   y: c   }, { x: L-c, y: c   },
        { x: c,   y: mid }, { x: L-c, y: mid },
        { x: c,   y: L-c }, { x: L-c, y: L-c },
      ];
    case 8:
      // 4 corners + 4 mid-edge diffusers (most balanced coverage)
      return [
        { x: c,   y: c   }, { x: L-c, y: c   },
        { x: c,   y: L-c }, { x: L-c, y: L-c },
        { x: mid, y: c   }, { x: mid, y: L-c },
        { x: c,   y: mid }, { x: L-c, y: mid },
      ];
    case 10: {
      // 4 corners + 4 mid-edge + 2 inner along centre row at 1/3 & 2/3 of inner span
      const t = c + (L - 2*c) / 3;
      return [
        { x: c,   y: c   }, { x: L-c, y: c   },
        { x: c,   y: L-c }, { x: L-c, y: L-c },
        { x: mid, y: c   }, { x: mid, y: L-c },
        { x: c,   y: mid }, { x: L-c, y: mid },
        { x: t,   y: mid }, { x: L-t, y: mid },
      ];
    }
    case 12: {
      // 3×4 grid — 3 cols (c, mid, L-c), 4 rows (c, c+s, L-c-s, L-c)
      const s    = (L - 2*c) / 3;
      const rows = [c, c+s, L-c-s, L-c];
      return rows.flatMap(ry => [
        { x: c,   y: ry },
        { x: mid, y: ry },
        { x: L-c, y: ry },
      ]);
    }
  }
}

/**
 * Minimum centre-to-centre distance between any two adjacent diffusers.
 * Used to derive the influence radius per diffuser.
 */
function minDiffuserSpacingM(count: DiffQty, cornerOffset: number): number {
  const L   = FRAME_SIZE;
  const c   = clampCornerOffset(cornerOffset);
  const inn = L - 2*c;   // inner span (corner-to-corner)
  switch (count) {
    case 4:  return inn;          // all neighbours are inn apart
    case 6:  return L/2 - c;     // row spacing = inn/2
    case 8:  return L/2 - c;     // corner → mid-edge = inn/2
    case 10: return inn / 3;     // inner pair + mid-edge → inner
    case 12: return inn / 3;     // row spacing in 3×4 grid
    default: return inn;
  }
}

// ─── Diffuser types (static base — dynamic list built inside component) ───────
// NOTE: DIFF_TYPES is now built dynamically via buildDiffTypes() inside the
//       component so the Custom Diffuser entry can be added/removed at runtime.

// ─── State ────────────────────────────────────────────────────────────────────

interface ModuleDesign {
  // Module spec
  diffuserCount:   DiffQty;
  cornerOffsetM:   number;   // distance from frame edge to nearest diffuser centre (m)
  mountingHeightM: number;

  // Diffuser spec
  diffuserType:    string;
  diffuserDiamMm:  number;
  poreSizeUm:      number;
  airflowPerDiffNm3h: number;
  diffuserDropKpa: number;

  // Water conditions
  waterDepthM:     number;
  waterTempC:      number;
  currentDoMgL:    number;
  alphaFactor:     number;
  betaFactor:      number;

  // Sediment characterisation
  sedimentD50Um:   number;   // median particle size (µm) — drives Shields criterion
  sedimentRho:     number;   // particle density (kg/m³)

  // Coverage assessment
  reservoirAreaHa: number;
  targetCoveragePct: number;
  sodGO2m2d:       number;

  // Blower
  blowerEff:       number;
  safetyPct:       number;

  // ── Tube-specific (active when diffuserType is *_porous_tube) ──
  tubeLengthM?:        number;   // 0.5 - 2.0 m (max = frame side)
  tubeCount?:          number;   // 1, 2 or 4 tubes per module
  tubeDiamMm?:         number;   // 50 - 100 mm
  tubePattern?:        'perimeter' | 'parallel' | 'cross' | 'centre_radial';
  tubeCentreOffsetM?:  number;   // distance from frame centre (parallel pattern)
  porePerCm?:          number;   // 6 - 12 pores per cm along tube
}

const DEFAULTS: ModuleDesign = {
  diffuserCount:   8,
  cornerOffsetM:   0.35,   // 350 mm from corner → 1.30 m inner span
  mountingHeightM: 0.40,
  diffuserType:    'membrane_disc_epdm',
  diffuserDiamMm:  300,
  poreSizeUm:      80,
  airflowPerDiffNm3h: 1.2,
  diffuserDropKpa: 1.5,
  waterDepthM:     5.0,
  waterTempC:      15,
  currentDoMgL:    2.0,
  alphaFactor:     0.80,
  betaFactor:      0.98,
  sedimentD50Um:   100,    // fine silt — typical UK lowland reservoir bed
  sedimentRho:     2650,   // silica/clay particle density
  reservoirAreaHa: 5.0,
  targetCoveragePct: 60,
  sodGO2m2d:       1.0,
  blowerEff:       0.70,
  safetyPct:       10,
  // Tube defaults (used when a tube diffuser is selected)
  tubeLengthM:        1.80,        // 1.8 m of 2 m frame side
  tubeCount:          2,           // 2 parallel tubes
  tubeDiamMm:         75,          // 75 mm HDPE
  tubePattern:        'parallel',
  tubeCentreOffsetM:  0.50,        // 0.5 m each side of centre
  porePerCm:          8,           // 8 pores/cm
};

// ─── SVG Module Illustration ──────────────────────────────────────────────────

interface TubeSegmentRender { x1: number; y1: number; x2: number; y2: number }
function ModuleSVG({ count, cornerOffset, positions, diffDiamMm, highlightIdx, rInfM, edgeCoverM,
                     isTube, tubeSegments, tubeDiamMm, porePerCm }:
  { count: DiffQty; cornerOffset: number; positions: DiffPosition[]; diffDiamMm: number;
    highlightIdx: number | null; rInfM: number; edgeCoverM: number;
    isTube?: boolean; tubeSegments?: TubeSegmentRender[]; tubeDiamMm?: number; porePerCm?: number }) {
  const SVG_SIZE = 300;
  const PAD = 28;
  const DRAW = SVG_SIZE - PAD * 2;
  const scale = DRAW / FRAME_SIZE;

  const toSvg = (m: number) => PAD + m * scale;
  const diffR = Math.max(8, (diffDiamMm / 1000) * scale * 1.8);
  const rInfPx = rInfM * scale;
  const cPx    = cornerOffset * scale;

  return (
    <svg width={SVG_SIZE} height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      className="w-full max-w-[300px] mx-auto rounded-xl">
      {/* Background */}
      <rect x={0} y={0} width={SVG_SIZE} height={SVG_SIZE} fill="#0f172a" rx={12} />

      {/* Water depth indicator (side bar) */}
      <rect x={4} y={PAD} width={6} height={DRAW} fill="#1e3a5f" rx={3} />
      <text x={7} y={PAD - 6} textAnchor="middle" fontSize={8} fill="#475569">depth</text>
      <text x={7} y={PAD + DRAW + 10} textAnchor="middle" fontSize={8} fill="#475569">bed</text>

      {/* HDPE frame */}
      <rect x={toSvg(0)} y={toSvg(0)} width={DRAW} height={DRAW}
        fill="none" stroke="#0ea5e9" strokeWidth={3} rx={4} />

      {/* Frame dimension labels */}
      <text x={toSvg(0) + 4} y={toSvg(0) - 6} fontSize={8} fill="#38bdf8">2.0 m</text>
      <text x={toSvg(0) - 22} y={toSvg(0) + DRAW / 2} fontSize={8} fill="#38bdf8"
        transform={`rotate(-90,${toSvg(0) - 22},${toSvg(0) + DRAW / 2})`}>2.0 m</text>

      {/* Corner offset annotation — dashed orange lines from TL corner to first diffuser */}
      <line x1={toSvg(0)} y1={toSvg(0)} x2={toSvg(positions[0]?.x ?? cornerOffset)} y2={toSvg(0)}
        stroke="#fb923c" strokeWidth={1} strokeDasharray="2,2" />
      <line x1={toSvg(0)} y1={toSvg(0)} x2={toSvg(0)} y2={toSvg(positions[0]?.y ?? cornerOffset)}
        stroke="#fb923c" strokeWidth={1} strokeDasharray="2,2" />
      <text x={toSvg(0) + cPx / 2} y={toSvg(0) - 3} textAnchor="middle" fontSize={7} fill="#fb923c">
        {cornerOffset.toFixed(2)}m
      </text>

      {/* Influence radius circles for corner diffusers (shown only when edgeCover > 0) */}
      {edgeCoverM > 0.005 && positions.filter((p, i) => i < 4).map((pos, i) => (
        <circle key={`inf-${i}`}
          cx={toSvg(pos.x)} cy={toSvg(pos.y)} r={rInfPx}
          fill="none" stroke="#06b6d4" strokeWidth={1} strokeDasharray="3,3" opacity={0.35} />
      ))}

      {/* Min-spacing guide lines between adjacent pairs */}
      {positions.slice(0, Math.min(2, positions.length)).map((p, i) =>
        positions.slice(i + 1).map((q, j) => {
          const dx = Math.abs(p.x - q.x);
          const dy = Math.abs(p.y - q.y);
          if ((dx < 0.02 || dy < 0.02) && dx + dy < FRAME_SIZE * 0.75) {
            return (
              <line key={`${i}-${j}`}
                x1={toSvg(p.x)} y1={toSvg(p.y)} x2={toSvg(q.x)} y2={toSvg(q.y)}
                stroke="#334155" strokeWidth={1} strokeDasharray="3,3" />
            );
          }
          return null;
        })
      )}

      {/* Air header pipe (ring around edge of frame, inset) */}
      <rect x={toSvg(0.07)} y={toSvg(0.07)} width={DRAW - 14} height={DRAW - 14}
        fill="none" stroke="#78350f" strokeWidth={2} strokeDasharray="4,3" rx={3} />
      <text x={toSvg(0.12)} y={toSvg(0.14)} fontSize={7} fill="#92400e">air header</text>

      {/* Diffusers — type-aware rendering: DISCS or TUBES */}
      {isTube && tubeSegments ? (
        // ─── TUBE rendering ───
        tubeSegments.map((seg, idx) => {
          const x1s = toSvg(seg.x1), y1s = toSvg(seg.y1);
          const x2s = toSvg(seg.x2), y2s = toSvg(seg.y2);
          const tubeW = Math.max(4, (tubeDiamMm ?? 75) / 1000 * scale * 2);
          const isHl = highlightIdx === idx;
          // Plume "curtain" perpendicular to the tube — show as semi-transparent rectangle
          const dx = seg.x2 - seg.x1, dy = seg.y2 - seg.y1;
          const len = Math.hypot(dx, dy);
          const nx = -dy / len, ny = dx / len;  // unit normal
          const plumeH = rInfPx * 1.4;
          const p1x = x1s + nx * plumeH, p1y = y1s + ny * plumeH;
          const p2x = x1s - nx * plumeH, p2y = y1s - ny * plumeH;
          const p3x = x2s - nx * plumeH, p3y = y2s - ny * plumeH;
          const p4x = x2s + nx * plumeH, p4y = y2s + ny * plumeH;
          return (
            <g key={`tube-${idx}`}>
              {/* Plume curtain (rectangular planar shape) */}
              <polygon points={`${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y} ${p4x},${p4y}`}
                fill={isHl ? '#06b6d440' : '#06b6d418'} />
              {/* Tube body */}
              <line x1={x1s} y1={y1s} x2={x2s} y2={y2s}
                stroke={isHl ? '#06b6d4' : '#0e7490'} strokeWidth={tubeW} strokeLinecap="round" />
              {/* Pore dots along the tube */}
              {(() => {
                const nDots = Math.max(6, Math.min(20, Math.round(len * (porePerCm ?? 8) / 5)));
                const dots: React.ReactNode[] = [];
                for (let p = 1; p <= nDots; p++) {
                  const t = p / (nDots + 1);
                  dots.push(
                    <circle key={p} cx={x1s + dx * t} cy={y1s + dy * t} r={1.1} fill="#0c4a6e" />
                  );
                }
                return dots;
              })()}
              {/* Tube label */}
              <text x={(x1s + x2s) / 2} y={(y1s + y2s) / 2 - tubeW / 2 - 4}
                textAnchor="middle" fontSize={7}
                fill={isHl ? '#67e8f9' : '#0891b2'}>T{idx + 1}</text>
            </g>
          );
        })
      ) : (
        // ─── DISC rendering (original) ───
        positions.map((pos, idx) => {
          const cx = toSvg(pos.x);
          const cy = toSvg(pos.y);
          const isHl = highlightIdx === idx;
          return (
            <g key={idx}>
              {/* Bubble plume — conical (point source) */}
              <ellipse cx={cx} cy={cy} rx={diffR * 0.9} ry={diffR * 3.5}
                fill={isHl ? '#8b5cf640' : '#7c3aed18'} />
              {/* Disc body */}
              <circle cx={cx} cy={cy} r={diffR}
                fill={isHl ? '#7c3aed' : '#4c1d95'}
                stroke={isHl ? '#a78bfa' : '#6d28d9'} strokeWidth={1.5} />
              {/* Membrane hatching */}
              {[...Array(4)].map((_, li) => (
                <line key={li}
                  x1={cx - diffR * 0.6} y1={cy - diffR * 0.4 + li * diffR * 0.3}
                  x2={cx + diffR * 0.6} y2={cy - diffR * 0.4 + li * diffR * 0.3}
                  stroke={isHl ? '#c4b5fd' : '#7c3aed'} strokeWidth={0.8} opacity={0.6} />
              ))}
              {/* Index label */}
              <text x={cx} y={cy + diffR + 9} textAnchor="middle" fontSize={7}
                fill={isHl ? '#c4b5fd' : '#6d28d9'}>D{idx + 1}</text>
            </g>
          );
        })
      )}

      {/* Frame leg symbols (corners) */}
      {[[0.03, 0.03], [1.97, 0.03], [0.03, 1.97], [1.97, 1.97]].map(([lx, ly], i) => (
        <g key={i}>
          <rect x={toSvg(lx) - 4} y={toSvg(ly) - 4} width={8} height={8}
            fill="#1e3a5f" stroke="#0ea5e9" strokeWidth={1} />
        </g>
      ))}

      {/* Legend */}
      <circle cx={PAD + 8} cy={SVG_SIZE - 14} r={5} fill="#4c1d95" stroke="#6d28d9" strokeWidth={1} />
      <text x={PAD + 18} y={SVG_SIZE - 11} fontSize={7} fill="#94a3b8">Diffuser</text>
      <rect x={PAD + 60} y={SVG_SIZE - 18} width={8} height={8} fill="#1e3a5f" stroke="#0ea5e9" strokeWidth={1} />
      <text x={PAD + 73} y={SVG_SIZE - 11} fontSize={7} fill="#94a3b8">Frame</text>
      <line x1={PAD + 110} y1={SVG_SIZE - 14} x2={PAD + 118} y2={SVG_SIZE - 14}
        stroke="#78350f" strokeWidth={2} strokeDasharray="3,2" />
      <text x={PAD + 122} y={SVG_SIZE - 11} fontSize={7} fill="#94a3b8">Air pipe</text>
    </svg>
  );
}

// ─── Small numeric input ──────────────────────────────────────────────────────

function NI({ label, value, onChange, min, max, step, unit, tip }:
  { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; tip?: string }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
        {label}{unit && <span className="font-normal text-slate-600 ml-1">({unit})</span>}
      </label>
      {tip && <p className="text-[9px] text-slate-600 mb-1">{tip}</p>}
      <input type="number" value={value} step={step ?? 0.1} min={min} max={max}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500" />
    </div>
  );
}

function Card({ title, icon: Icon, iconColor, children }:
  { title: string; icon: React.ElementType; iconColor: string; children: React.ReactNode }) {
  return (
    <div className="rb-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── Custom diffuser integration ──────────────────────────────────────────────

const CUSTOM_DIFF_EXPORT_KEY = 'rb_custom_diffuser_export_v1';

interface CustomExport {
  value: string; label: string; sote: number; poreSize: number;
  diameterMm: number; activePores: number; shape: string;
  alpha: number; beta: number;
  r_diff: number; r_surface: number; r_inf_bed: number; notes: string;
  // Tube/strip-specific (when shape is tube or strip)
  lengthMm?: number;
  widthMm?: number;
  coverageBedM2?: number;
  coverageSurfM2?: number;
}

function loadCustomDiffuser(): CustomExport | null {
  try {
    const raw = sessionStorage.getItem(CUSTOM_DIFF_EXPORT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** Build diffuser type list including custom if saved */
function buildDiffTypes(custom: CustomExport | null) {
  const base = [
    { value: 'membrane_disc_epdm', label: 'EPDM Membrane Disc', sote: 6.5,
      pores: (d: number) => Math.round(0.35 * Math.PI * (d/2)**2),
      notes: 'Fine pore 50–250 µm, SAE 5–7 kgO₂/kWh, most common in reservoirs.' },
    { value: 'membrane_disc_ptfe', label: 'PTFE Membrane Disc', sote: 7.0,
      pores: (d: number) => Math.round(0.40 * Math.PI * (d/2)**2),
      notes: 'Very fine 40–150 µm, best OTE, high chemical resistance.' },
    { value: 'ceramic_dome',       label: 'Ceramic Dome',        sote: 5.5,
      pores: (d: number) => Math.round(0.10 * Math.PI * (d/2)**2),
      notes: 'Robust, pore 100–400 µm, lower OTE, suited to warm/aggressive water.' },
    { value: 'hdpe_porous_tube',   label: 'HDPE Porous Tube',   sote: 5.0,
      pores: (d: number) => Math.round(0.08 * d * 500),
      notes: 'Low cost, coarser pores, mixing-priority applications.' },
  ];
  if (custom) {
    const c = custom;
    base.push({
      value: 'custom',
      label: `⭐ ${c.label}`,
      sote:  c.sote,
      // pores() uses fixed activePores from custom design — diameter arg ignored
      pores: (_d: number) => c.activePores,
      notes: c.notes,
    });
  }
  return base;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DiffuserModulePage() {
  const navigate = useNavigate();

  // Load custom diffuser (may be null if user hasn't designed one yet)
  const [customDiff, setCustomDiff] = useState<CustomExport | null>(loadCustomDiffuser);

  // Refresh custom diff when page becomes visible (user may have just returned from CustomDiffuserPage)
  useEffect(() => {
    const onFocus = () => setCustomDiff(loadCustomDiffuser());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Dynamic diffuser type list
  const DIFF_TYPES = useMemo(() => buildDiffTypes(customDiff), [customDiff]);

  const [d, setD]   = useState<ModuleDesign>(() => loadPersisted<ModuleDesign>(KEYS.DIFFUSER_MODULE, DEFAULTS));
  const [highlightIdx, setHighlightIdx] = useState<number | null>(null);
  const [showCoverage, setShowCoverage] = useState(true);

  const upd = (p: Partial<ModuleDesign>) => setD(prev => ({ ...prev, ...p }));

  // Persist to both sessionStorage AND localStorage on every change (shared across all pages)
  useEffect(() => {
    savePersisted(KEYS.DIFFUSER_MODULE, d);
  }, [d]);

  const COUNTS: DiffQty[] = [4, 6, 8, 10, 12];

  // ── Calculations ────────────────────────────────────────────────────────────
  const R = useMemo(() => {
    const T            = d.waterTempC;
    const count        = d.diffuserCount;
    const cornerOffset = clampCornerOffset(d.cornerOffsetM);
    const positions    = diffuserLayout(count, cornerOffset);

    // ── Layout geometry ──────────────────────────────────────────────────────
    const innerSpanM      = FRAME_SIZE - 2 * cornerOffset;  // corner-to-corner
    const s_min           = minDiffuserSpacingM(count, cornerOffset); // closest pair
    const r_inf           = s_min / 2;                       // influence radius per diffuser
    const edgeCoverM      = Math.max(0, r_inf - cornerOffset); // coverage beyond frame
    const effectivePitchM = FRAME_SIZE + 2 * edgeCoverM;    // module deployment pitch
    const influenceAreaM2 = effectivePitchM ** 2;            // effective area per module
    const modulesPerHa    = 10000 / influenceAreaM2;

    // Diffuser type data — DIFF_TYPES is dynamic (may include custom)
    const dtype       = DIFF_TYPES.find(t => t.value === d.diffuserType) ?? DIFF_TYPES[0];
    // Pore count: for TUBE diffusers use porePerCm × tubeLength; for DISC use legacy formula
    const isTube       = d.diffuserType === 'hdpe_porous_tube' || d.diffuserType === 'epdm_porous_tube';
    const activePores  = isTube
      ? Math.max(1, Math.round((d.porePerCm ?? 8) * (d.tubeLengthM ?? 1.8) * 100))
      : Math.max(1, dtype.pores(d.diffuserDiamMm));

    // If custom diffuser selected, use its pore size and diameter from export record
    const isCustom     = d.diffuserType === 'custom' && customDiff != null;
    const activePoreSize = isCustom ? customDiff!.poreSize   : d.poreSizeUm;
    const activeDiamMm   = isCustom ? customDiff!.diameterMm : d.diffuserDiamMm;
    const customNote     = isCustom ? ` [Custom: ${customDiff!.label}]` : '';

    // Effective submergence
    const effSubm = Math.max(0.5, d.waterDepthM - d.mountingHeightM);
    const rho_w   = waterDensity(T);
    const P_bed   = P_ATM + rho_w * G * effSubm;

    // Per-diffuser bubble physics (clean water, no disturbance)
    const d_b_mm   = bubbleDiamMm(activePoreSize, d.airflowPerDiffNm3h, activePores, T);
    const d_b_surf = bubbleSurface(d_b_mm, effSubm, T);
    const v_rise_b = riseVelocityMs(d_b_mm, T);
    const v_rise_s = riseVelocityMs(d_b_surf, T);
    const riseTimeS= effSubm / ((v_rise_b + v_rise_s) / 2);
    const Re       = (rho_w * v_rise_b * (d_b_mm / 1000)) / waterViscosity(T);

    // ── Bed sedimentation disturbance (Shields criterion) ───────────────────
    // Induced horizontal bed velocity from rising bubble plume (Seol et al. 2007)
    const q_act_bed = (d.airflowPerDiffNm3h / 3600) * (P_ATM / P_bed); // m³/s at depth
    const B_buoy    = G * q_act_bed;                                     // buoyancy flux m⁴/s³
    const U_plume   = 1.46 * Math.pow(B_buoy * effSubm, 1/3);
    const v_bed     = 0.15 * U_plume / Math.sqrt(Math.max(effSubm, 1)); // m/s horizontal

    // Shields parameter: θ = τ_bed / ((ρ_s−ρ_w)·g·d₅₀)
    const d50_m      = d.sedimentD50Um / 1e6;
    const C_D_bed    = 0.01;                            // bed drag coefficient (rough turbulent)
    const tau_bed    = rho_w * C_D_bed * v_bed ** 2;   // bed shear stress (Pa)
    const u_star     = Math.sqrt(tau_bed / rho_w);      // shear velocity (m/s)
    const shields    = tau_bed / ((d.sedimentRho - rho_w) * G * Math.max(d50_m, 1e-8));
    const shields_cr = 0.047;                            // critical Shields (non-cohesive Shields 1936)
    const disturbRatio  = shields / shields_cr;
    const resuspRisk = disturbRatio > 1.5 ? 'High' : disturbRatio > 1.0 ? 'Moderate'
                     : disturbRatio > 0.5 ? 'Low' : 'Negligible';
    const resuspColor= disturbRatio > 1.5 ? 'text-red-400' : disturbRatio > 1.0 ? 'text-amber-400'
                     : disturbRatio > 0.5 ? 'text-yellow-400' : 'text-emerald-400';

    // Bubble size correction from near-bed turbulence (Loubière & Hébrard 2003)
    // Turbulent kinetic energy at orifice promotes bubble coalescence → +0–15% diameter
    const turbCorr      = Math.min(0.15, 0.08 * Math.min(disturbRatio, 2));
    const d_b_mm_dist   = d_b_mm * (1 + turbCorr);
    const d_b_surf_dist = bubbleSurface(d_b_mm_dist, effSubm, T);

    // Enhanced SOD: resuspended sediment releases stored O₂ demand
    // +50% per unit disturbRatio above threshold, capped at 3×
    const sodResuspFactor = disturbRatio > 1 ? Math.min(3.0, 1 + 0.5 * (disturbRatio - 1)) : 1.0;
    const effectiveSOD    = d.sodGO2m2d * sodResuspFactor; // g O₂/m²/day

    // Per-module oxygen transfer
    const sote_pct   = Math.min(99, dtype.sote * effSubm);
    const modAirNm3h = count * d.airflowPerDiffNm3h;
    const modSOTR    = calcSOTR(modAirNm3h, sote_pct / 100);
    const modAOTR    = calcAOTR(modSOTR, d.alphaFactor, d.betaFactor, T, d.currentDoMgL);

    // Coverage assessment (using disturbed SOD)
    const reservoirAreaM2  = d.reservoirAreaHa * 1e4;
    const targetAreaM2     = reservoirAreaM2 * d.targetCoveragePct / 100;
    const modulesRequired  = Math.ceil(targetAreaM2 / influenceAreaM2);
    const totalAirNm3h     = modulesRequired * modAirNm3h;
    const totalSOTR        = modulesRequired * modSOTR;
    const totalAOTR        = modulesRequired * modAOTR;
    const coveredAreaM2    = Math.min(reservoirAreaM2, modulesRequired * influenceAreaM2);
    const sodKgO2h_nom     = (d.sodGO2m2d   * coveredAreaM2 / 1000) / 24; // nominal
    const sodKgO2h         = (effectiveSOD   * coveredAreaM2 / 1000) / 24; // with disturbance
    const oxygenSurplus    = totalAOTR - sodKgO2h;

    // Blower duty
    const blowerPowerKW = blowerKw(totalAirNm3h, effSubm, d.diffuserDropKpa, d.safetyPct, d.blowerEff);
    const sae = totalAOTR > 0 && blowerPowerKW > 0 ? totalAOTR / blowerPowerKW : 0;

    // Water properties at T
    const sigma_w = waterSurfaceTension(T);
    const mu_w    = waterViscosity(T);
    const nu_w    = mu_w / rho_w;

    return {
      T, count, cornerOffset, positions,
      innerSpanM, s_min, r_inf, edgeCoverM, effectivePitchM, influenceAreaM2, modulesPerHa,
      activePores, activePoreSize, activeDiamMm, isCustom, customNote, effSubm, P_bed, rho_w,
      d_b_mm, d_b_surf, v_rise_b, v_rise_s, riseTimeS, Re,
      v_bed, tau_bed, u_star, shields, shields_cr, disturbRatio,
      resuspRisk, resuspColor,
      turbCorr, d_b_mm_dist, d_b_surf_dist,
      sodResuspFactor, effectiveSOD,
      sote_pct, modAirNm3h, modSOTR, modAOTR,
      reservoirAreaM2, targetAreaM2, modulesRequired,
      totalAirNm3h, totalSOTR, totalAOTR,
      coveredAreaM2, sodKgO2h_nom, sodKgO2h, oxygenSurplus,
      blowerPowerKW, sae,
      sigma_w, mu_w, nu_w,
    };
  }, [d, DIFF_TYPES, customDiff]);

  const fmt = (v: number, dp = 2) => isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp);

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      <PageHeader
        icon={Layers}
        iconColor="text-violet-400"
        title="Diffuser Module Design"
        subtitle="2 m × 2 m submerged HDPE frame — per-module testing & reservoir coverage"
        badge={
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-violet-500/10 text-violet-400 border border-violet-800/40">
            {R.count} diffusers / module
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/diffuser-validation')}
              className="px-3 py-1.5 text-xs font-bold text-sky-300 bg-rb-elevated border border-sky-700/60 hover:border-sky-500 rounded-lg flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Validation Report
            </button>
            <button onClick={() => navigate('/module-array')}
              className="px-3 py-1.5 text-xs font-bold text-cyan-300 bg-rb-elevated border border-cyan-700/60 hover:border-cyan-500 rounded-lg flex items-center gap-1.5">
              <Grid3x3 className="w-3.5 h-3.5" /> Module Array
            </button>
            <button onClick={() => navigate('/reservoir-aeration-spec')}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <Wind className="w-3.5 h-3.5" /> Aeration Spec
            </button>
            <button onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button onClick={() => { setD(DEFAULTS); sessionStorage.removeItem(SESSION_KEY); toast.info('Reset to defaults'); }}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          { label: 'Diffusers/module', val: String(R.count),               unit: 'ea',          col: '#06b6d4' },
          { label: 'Bubble ∅ bed',     val: fmt(R.d_b_mm_dist, 3),        unit: 'mm disturbed', col: '#8b5cf6', warn: R.d_b_mm_dist > 2 },
          { label: 'Bubble ∅ surface', val: fmt(R.d_b_surf_dist, 3),      unit: `mm ×${fmt(R.d_b_surf_dist/Math.max(R.d_b_mm_dist,0.001),2)}`, col: '#a78bfa' },
          { label: 'Rise velocity',    val: fmt(R.v_rise_b, 3),            unit: 'm/s at bed',  col: '#10b981', warn: R.v_rise_b > 0.35 },
          { label: 'Rise time',        val: fmt(R.riseTimeS, 1),           unit: 's to surface',col: '#38bdf8' },
          { label: 'Module AOTR',      val: fmt(R.modAOTR, 3),            unit: 'kg O₂/h',    col: '#f59e0b' },
          { label: 'Modules needed',   val: String(R.modulesRequired),      unit: `for ${d.targetCoveragePct}% cover`, col: '#f97316' },
          { label: 'Blower total',     val: fmt(R.blowerPowerKW, 1),       unit: 'kW',          col: '#ef4444' },
        ].map(k => (
          <div key={k.label} className={`rb-section px-3 py-2 relative ${k.warn ? 'border-amber-500/40' : ''}`}>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</div>
            <div className="text-lg font-bold font-mono" style={{ color: k.col }}>{k.val}</div>
            <div className="text-[10px] text-slate-600">{k.unit}</div>
            {k.warn && <AlertTriangle className="absolute top-2 right-2 w-3 h-3 text-amber-400" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* LEFT COLUMN: Module illustration + layout selector */}
        <div className="space-y-4">
          <Card title="Module Frame (Plan View — 2 m × 2 m)" icon={Layers} iconColor="text-violet-400">
            <div className="mb-4">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Diffusers per Module</div>
              <div className="flex gap-2 flex-wrap">
                {COUNTS.map(n => (
                  <button key={n} onClick={() => upd({ diffuserCount: n })}
                    className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${
                      d.diffuserCount === n
                        ? 'bg-violet-500/15 border-violet-500/60 text-violet-300'
                        : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {(() => {
              // Detect tube/strip — for standard tube types OR for custom diffuser whose shape is tube/strip
              const customShapeIsLine = d.diffuserType === 'custom'
                && customDiff != null
                && (customDiff.shape === 'tube' || customDiff.shape === 'strip');
              const isTubeType = d.diffuserType === 'hdpe_porous_tube'
                || d.diffuserType === 'epdm_porous_tube'
                || customShapeIsLine;
              let tubeSegs: TubeSegmentRender[] = [];
              if (isTubeType) {
                // For custom-tube: use the custom diffuser's length and tubeCount = 1 (one tube as designed)
                // For standard tube types: use the module's tube parameters
                const isCustomLine = d.diffuserType === 'custom' && customDiff
                  && (customDiff.shape === 'tube' || customDiff.shape === 'strip');
                const L = isCustomLine
                  ? Math.min(2.0, Math.max(0.3, (customDiff!.lengthMm ?? 1800) / 1000))
                  : Math.min(2.0, d.tubeLengthM ?? 1.8);
                const tubeCount = isCustomLine ? 1 : Math.min(4, Math.max(1, d.tubeCount ?? 2));
                const pat = d.tubePattern ?? 'parallel';
                const off = Math.max(0.05, Math.min(0.95, d.tubeCentreOffsetM ?? 0.5));
                const c = (FRAME_SIZE - L) / 2;
                if (pat === 'perimeter') {
                  const inset = 0.1;
                  const segs = [
                    { x1: c, y1: inset, x2: c + L, y2: inset },
                    { x1: c, y1: FRAME_SIZE - inset, x2: c + L, y2: FRAME_SIZE - inset },
                    { x1: inset, y1: c, x2: inset, y2: c + L },
                    { x1: FRAME_SIZE - inset, y1: c, x2: FRAME_SIZE - inset, y2: c + L },
                  ];
                  tubeSegs = segs.slice(0, tubeCount);
                } else if (pat === 'parallel') {
                  if (tubeCount === 1) {
                    tubeSegs = [{ x1: c, y1: FRAME_SIZE / 2, x2: c + L, y2: FRAME_SIZE / 2 }];
                  } else {
                    for (let i = 0; i < tubeCount; i++) {
                      const sign = (i % 2 === 0) ? -1 : 1;
                      const idx = Math.floor(i / 2) + 1;
                      const y = FRAME_SIZE / 2 + sign * off * idx;
                      tubeSegs.push({ x1: c, y1: y, x2: c + L, y2: y });
                    }
                  }
                } else if (pat === 'cross') {
                  const mid = FRAME_SIZE / 2;
                  tubeSegs = [
                    { x1: c, y1: mid, x2: c + L, y2: mid },
                    { x1: mid, y1: c, x2: mid, y2: c + L },
                  ].slice(0, tubeCount);
                } else if (pat === 'centre_radial') {
                  const mid = FRAME_SIZE / 2;
                  for (let i = 0; i < tubeCount; i++) {
                    const ang = (i / tubeCount) * 2 * Math.PI;
                    tubeSegs.push({
                      x1: mid, y1: mid,
                      x2: mid + Math.cos(ang) * L / 2, y2: mid + Math.sin(ang) * L / 2,
                    });
                  }
                }
              }
              const isCustomLine2 = d.diffuserType === 'custom' && customDiff
                && (customDiff.shape === 'tube' || customDiff.shape === 'strip');
              const renderedTubeDiam = isCustomLine2
                ? (customDiff!.diameterMm ?? 75)
                : (d.tubeDiamMm ?? 75);
              return (
                <ModuleSVG
                  count={R.count}
                  cornerOffset={R.cornerOffset}
                  positions={R.positions}
                  diffDiamMm={d.diffuserDiamMm}
                  highlightIdx={highlightIdx}
                  rInfM={R.r_inf}
                  edgeCoverM={R.edgeCoverM}
                  isTube={isTubeType}
                  tubeSegments={tubeSegs}
                  tubeDiamMm={renderedTubeDiam}
                  porePerCm={d.porePerCm ?? 8}
                />
              );
            })()}

            {/* Diffuser list with hover highlight */}
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-1">
              {R.positions.map((_, i) => (
                <button key={i}
                  onMouseEnter={() => setHighlightIdx(i)}
                  onMouseLeave={() => setHighlightIdx(null)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-mono border transition-all ${
                    highlightIdx === i
                      ? 'bg-violet-500/20 border-violet-500/50 text-violet-300'
                      : 'bg-rb-elevated border-slate-800/50 text-slate-600 hover:text-slate-400'
                  }`}>
                  D{i + 1}
                </button>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <NI label="Corner Offset" unit="m" value={d.cornerOffsetM}
                onChange={v => upd({ cornerOffsetM: v })} step={0.05} min={0.10} max={0.75}
                tip="Distance from frame edge to nearest diffuser centre. Controls inner span and edge-coverage extension." />
              <div className="rb-section px-3 py-2">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Inner span</div>
                <div className="text-lg font-bold font-mono text-cyan-400">{fmt(R.innerSpanM, 2)} m</div>
                <div className="text-[10px] text-slate-600">corner ↔ corner = 2.0 − 2×{fmt(R.cornerOffset, 2)}</div>
              </div>
            </div>

            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-500 grid grid-cols-2 gap-2">
              <div>Frame area <strong className="text-slate-300">4.00 m²</strong></div>
              <div>Min diffuser spacing <strong className="text-violet-400">{fmt(R.s_min, 3)} m</strong></div>
              <div>Influence radius / diff. <strong className="text-cyan-400">{fmt(R.r_inf, 3)} m</strong></div>
              <div>Edge coverage <strong className={R.edgeCoverM > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                {R.edgeCoverM > 0 ? `+${fmt(R.edgeCoverM, 3)} m` : 'within frame'}
              </strong></div>
              <div>Effective pitch <strong className="text-amber-400">{fmt(R.effectivePitchM, 3)} m</strong></div>
              <div>Modules / ha <strong className="text-orange-400">{fmt(R.modulesPerHa, 1)}</strong></div>
            </div>
          </Card>

          {/* Module mounting & conditions */}
          <Card title="Water Conditions" icon={Thermometer} iconColor="text-sky-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <NI label="Water Depth" unit="m" value={d.waterDepthM}
                onChange={v => upd({ waterDepthM: v })} step={0.5} min={1} />
              <NI label="Mounting Height" unit="m" value={d.mountingHeightM}
                onChange={v => upd({ mountingHeightM: v })} step={0.05} min={0.1} max={1.0}
                tip="Frame leg height above bed. Default 0.4 m." />
              <NI label="Temperature" unit="°C" value={d.waterTempC}
                onChange={v => upd({ waterTempC: v })} step={1} min={0} max={35} />
              <NI label="Current DO" unit="mg/L" value={d.currentDoMgL}
                onChange={v => upd({ currentDoMgL: v })} step={0.1} min={0} max={14} />
              <NI label="α (alpha)" value={d.alphaFactor}
                onChange={v => upd({ alphaFactor: v })} step={0.01} min={0.3} max={1.0} />
              <NI label="β (beta)" value={d.betaFactor}
                onChange={v => upd({ betaFactor: v })} step={0.01} min={0.9} max={1.0} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-[10px] text-slate-500">
              <div>σ(T) = <strong className="text-slate-300 font-mono">{fmt(R.sigma_w * 1000, 2)}</strong> mN/m</div>
              <div>μ(T) = <strong className="text-slate-300 font-mono">{fmt(R.mu_w * 1000, 3)}</strong> mPa·s</div>
              <div>ν(T) = <strong className="text-slate-300 font-mono">{fmt(R.nu_w * 1e6, 3)}</strong> mm²/s</div>
            </div>
          </Card>
        </div>

        {/* RIGHT COLUMN: Diffuser spec + bubble physics + coverage */}
        <div className="space-y-4">

          {/* Diffuser specification */}
          <Card title="Diffuser Specification" icon={Circle} iconColor="text-violet-400">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              {DIFF_TYPES.map(t => (
                <button key={t.value} onClick={() => upd({ diffuserType: t.value })}
                  className={`text-left px-3 py-2 rounded-xl border text-xs transition-all ${
                    d.diffuserType === t.value
                      ? t.value === 'custom'
                        ? 'bg-emerald-500/10 border-emerald-500/60 text-emerald-300'
                        : 'bg-violet-500/10 border-violet-500/60 text-violet-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>
                  <div className="font-bold">{t.label}</div>
                  <div className="text-[9px] mt-0.5 opacity-70 line-clamp-1">{t.notes}</div>
                  <div className="text-[9px] mt-1 text-emerald-400 font-mono">SOTE {t.sote.toFixed(1)}%/m</div>
                </button>
              ))}
            </div>
            {/* Custom diffuser shortcut */}
            <button onClick={() => navigate('/custom-diffuser')}
              className="w-full mb-3 py-2 rounded-xl text-xs font-bold text-violet-300 bg-rb-elevated border border-violet-700/40 hover:border-violet-500 flex items-center justify-center gap-2 transition-all">
              <Sparkles className="w-3.5 h-3.5" />
              {customDiff ? `Edit custom: ${customDiff.label}` : 'Design a custom diffuser →'}
            </button>
            {/* Custom active notice */}
            {R.isCustom && customDiff && (
              <div className="mb-3 p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-[10px] text-emerald-300">
                <strong>Custom diffuser active:</strong> {customDiff.notes} — pore {fmt(R.activePoreSize,0)} µm · ∅{customDiff.diameterMm}mm · {customDiff.activePores.toLocaleString()} pores
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <NI label="Disc Diameter" unit="mm" value={d.diffuserDiamMm}
                onChange={v => upd({ diffuserDiamMm: v })} step={10} min={100} max={600}
                tip={R.isCustom ? 'Overridden by custom diffuser design' : 'Membrane disc diameter (mm)'} />
              <NI label="Pore Size" unit="µm" value={R.isCustom ? R.activePoreSize : d.poreSizeUm}
                onChange={v => !R.isCustom && upd({ poreSizeUm: v })} step={5} min={20} max={400}
                tip={R.isCustom ? 'Pore size from custom diffuser design (read-only)' : '< 100 µm = fine bubble.'} />
              <NI label="Airflow/diffuser" unit="Nm³/h" value={d.airflowPerDiffNm3h}
                onChange={v => upd({ airflowPerDiffNm3h: v })} step={0.1} min={0.1} max={10}
                tip="0.5–2 Nm³/h typical for fine-bubble discs." />
              <NI label="Diffuser ΔP" unit="kPa" value={d.diffuserDropKpa}
                onChange={v => upd({ diffuserDropKpa: v })} step={0.1} min={0} max={10} />
            </div>
            <div className="mt-2 text-[10px] text-slate-600 p-2 rounded-lg bg-rb-elevated">
              Active pores: <strong className="text-slate-400">{R.activePores.toLocaleString()}</strong>
              {' · '} Per-pore flow: <strong className="text-slate-400">
                {fmt((d.airflowPerDiffNm3h / 3600 / R.activePores) * 1e9, 2)} µL/s
              </strong>
              {' · '} SOTE: <strong className="text-emerald-400">{fmt(R.sote_pct, 1)}%</strong> at {fmt(R.effSubm, 1)} m submergence
            </div>

            {/* ── TUBE-SPECIFIC PARAMETERS (shown when a tube diffuser is selected) ── */}
            {(d.diffuserType === 'hdpe_porous_tube' || d.diffuserType === 'epdm_porous_tube') && (
              <div className="mt-3 p-3 rounded-xl border border-cyan-500/30 bg-cyan-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-3.5 h-3.5 text-cyan-300" />
                  <h4 className="text-xs font-bold text-cyan-200">Tube Diffuser Geometry — line-source physics</h4>
                </div>
                <p className="text-[10px] text-slate-400 mb-3 leading-relaxed">
                  Tube diffusers are <strong>line sources</strong> (Wüest 1992) — different plume physics from
                  discs (point sources). Tube length is bounded by frame side (max {fmt(2.0, 1)} m). Pattern selects
                  how tubes are arranged on the 2×2 m frame.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  <NI label="Tube Length" unit="m" value={d.tubeLengthM ?? 1.8}
                    onChange={v => upd({ tubeLengthM: Math.min(2.0, Math.max(0.3, v)) })}
                    step={0.1} min={0.3} max={2.0}
                    tip="Each tube length along the frame side. Max = 2.0 m (full side)." />
                  <NI label="Tubes per Module" value={d.tubeCount ?? 2}
                    onChange={v => upd({ tubeCount: Math.min(4, Math.max(1, Math.round(v))) })}
                    step={1} min={1} max={4}
                    tip="1 - 4 tubes per 2×2 m frame." />
                  <NI label="Tube Outer Diameter" unit="mm" value={d.tubeDiamMm ?? 75}
                    onChange={v => upd({ tubeDiamMm: v })} step={5} min={40} max={120}
                    tip="50 – 100 mm typical for HDPE / EPDM porous tube." />
                  <NI label="Pore Density" unit="/cm" value={d.porePerCm ?? 8}
                    onChange={v => upd({ porePerCm: v })} step={1} min={4} max={20}
                    tip="6 – 12 pores per cm of tube length (manufacturer spec)." />
                  <NI label="Centre Offset" unit="m" value={d.tubeCentreOffsetM ?? 0.5}
                    onChange={v => upd({ tubeCentreOffsetM: v })} step={0.05} min={0} max={0.95}
                    tip="Distance of parallel tubes from frame centre. 0 = on centre line." />
                </div>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {(['perimeter', 'parallel', 'cross', 'centre_radial'] as const).map(p => (
                    <button key={p} onClick={() => upd({ tubePattern: p })}
                      className={`text-[10px] px-2 py-1.5 rounded-lg transition-all capitalize ${
                        (d.tubePattern ?? 'parallel') === p
                          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40'
                          : 'text-slate-400 hover:text-slate-200 border border-slate-700/40'
                      }`}>
                      {p.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 p-2 rounded bg-slate-800/40">
                  <strong className="text-cyan-300">Total tube length:</strong>{' '}
                  <span className="font-mono">{((d.tubeLengthM ?? 1.8) * (d.tubeCount ?? 2)).toFixed(2)} m</span>{' '}
                  <strong className="text-cyan-300">· Pores per module:</strong>{' '}
                  <span className="font-mono">{Math.round((d.porePerCm ?? 8) * (d.tubeLengthM ?? 1.8) * 100 * (d.tubeCount ?? 2)).toLocaleString()}</span>{' '}
                  <strong className="text-cyan-300">· Bed footprint:</strong>{' '}
                  <span className="font-mono">≈ rectangular curtain (L+2r) × 2r</span>
                </div>
              </div>
            )}
          </Card>

          {/* Bubble physics */}
          <Card title="Per-Module Bubble Physics" icon={Activity} iconColor="text-emerald-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              {[
                { label: 'Bubble ∅ at orifice',  val: fmt(R.d_b_mm, 3),    unit: 'mm', col: R.d_b_mm < 1 ? '#10b981' : R.d_b_mm < 2 ? '#8b5cf6' : '#f59e0b',
                  note: R.d_b_mm < 1 ? '< 1 mm ✓' : R.d_b_mm < 2 ? 'fine bubble' : '⚠ medium bubble' },
                { label: 'Bubble ∅ at surface',   val: fmt(R.d_b_surf, 3),  unit: 'mm', col: '#a78bfa',
                  note: `×${fmt(R.d_b_surf/Math.max(R.d_b_mm,0.001),3)} expanded` },
                { label: 'Rise v at bed',          val: fmt(R.v_rise_b, 3),  unit: 'm/s', col: R.v_rise_b < 0.35 ? '#10b981' : '#f59e0b',
                  note: '< 0.35 m/s target' },
                { label: 'Rise v at surface',      val: fmt(R.v_rise_s, 3),  unit: 'm/s', col: '#34d399',
                  note: 'faster — bigger bubble' },
                { label: 'Rise time',              val: fmt(R.riseTimeS, 1), unit: 's',   col: '#38bdf8',
                  note: 'bed → surface' },
                { label: 'Reynolds number',        val: fmt(R.Re, 0),        unit: 'Re',  col: '#94a3b8',
                  note: R.Re < 1 ? 'Stokes' : R.Re < 1000 ? 'Intermediate' : 'Turbulent' },
              ].map(m => (
                <div key={m.label} className="rb-section px-3 py-2">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">{m.label}</div>
                  <div className="text-lg font-bold font-mono" style={{ color: m.col }}>{m.val}</div>
                  <div className="text-[10px] text-slate-600">{m.unit}</div>
                  <div className="text-[9px] mt-0.5" style={{ color: m.col + 'cc' }}>{m.note}</div>
                </div>
              ))}
            </div>

            {/* Depth profile table */}
            <div className="rounded-xl overflow-hidden border border-slate-800/50">
              <div className="px-3 py-1.5 text-[9px] font-bold text-slate-600 uppercase bg-rb-elevated border-b border-slate-800/50">
                Bubble Profile — Bed → Surface at {d.waterTempC} °C
              </div>
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-slate-600 border-b border-slate-800/40">
                    <th className="text-left px-2 py-1">Depth (m)</th>
                    <th className="text-center px-2 py-1">P (kPa)</th>
                    <th className="text-center px-2 py-1 text-violet-400">∅ (mm)</th>
                    <th className="text-center px-2 py-1 text-emerald-400">v (m/s)</th>
                    <th className="text-center px-2 py-1 text-sky-400">DO sat</th>
                  </tr>
                </thead>
                <tbody>
                  {[R.effSubm, R.effSubm * 0.75, R.effSubm * 0.5, R.effSubm * 0.25, 0.05].map((depth, i) => {
                    const P     = P_ATM + waterDensity(d.waterTempC) * G * depth;
                    const d_at  = R.d_b_mm * Math.pow(R.P_bed / P, 1 / 3);
                    const v_at  = riseVelocityMs(d_at, d.waterTempC);
                    const cs    = doSat(d.waterTempC) * (P / P_ATM);
                    const isBot = i === 0;
                    const isTop = i === 4;
                    return (
                      <tr key={i} className={`border-b border-slate-800/20 ${isBot ? 'bg-violet-500/5' : isTop ? 'bg-emerald-500/5' : ''}`}>
                        <td className="px-2 py-1 font-mono text-slate-400">
                          {depth.toFixed(2)}{isBot ? ' ← bed' : isTop ? ' ← surface' : ''}
                        </td>
                        <td className="px-2 py-1 text-center font-mono text-slate-500">{(P/1000).toFixed(1)}</td>
                        <td className="px-2 py-1 text-center font-mono font-bold text-violet-300">{d_at.toFixed(3)}</td>
                        <td className="px-2 py-1 text-center font-mono text-emerald-300">{v_at.toFixed(3)}</td>
                        <td className="px-2 py-1 text-center font-mono text-sky-300">{cs.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Per-module O₂ */}
            <div className="mt-3 grid grid-cols-3 gap-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-xs text-slate-400">
              <div>Air: <strong className="text-slate-200 font-mono">{fmt(R.modAirNm3h, 1)} Nm³/h</strong></div>
              <div>SOTR: <strong className="text-amber-400 font-mono">{fmt(R.modSOTR, 3)} kgO₂/h</strong></div>
              <div>AOTR: <strong className="text-orange-400 font-mono">{fmt(R.modAOTR, 3)} kgO₂/h</strong></div>
            </div>
          </Card>

          {/* Bed Sedimentation Disturbance */}
          <Card title="Bed Sedimentation Disturbance" icon={BarChart3} iconColor="text-orange-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              <NI label="Sediment D₅₀" unit="µm" value={d.sedimentD50Um}
                onChange={v => upd({ sedimentD50Um: v })} step={10} min={1} max={2000}
                tip="Median particle diameter. Fine silt ≈ 20–63 µm. Sand 63–2000 µm. Coarser → harder to disturb." />
              <NI label="Particle Density" unit="kg/m³" value={d.sedimentRho}
                onChange={v => upd({ sedimentRho: v })} step={50} min={1500} max={3000}
                tip="Silica/clay ≈ 2650, organic floc ≈ 1200–1800 kg/m³. Lighter particles disturb more easily." />
              <div className="rb-section px-3 py-2">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">v_bed (induced)</div>
                <div className="text-base font-bold font-mono text-amber-400">{fmt(R.v_bed * 1000, 2)} mm/s</div>
                <div className="text-[10px] text-slate-600">horiz. at sediment surface</div>
              </div>
            </div>

            {/* Shields number display */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {[
                { label: 'Bed shear stress',   val: fmt(R.tau_bed * 1000, 3), unit: 'mPa',   col: '#f97316' },
                { label: 'Shear velocity u*',  val: fmt(R.u_star * 1000, 3),  unit: 'mm/s',  col: '#fb923c' },
                { label: 'Shields θ',          val: fmt(R.shields, 4),         unit: '(calc)',col: R.shields > R.shields_cr ? '#ef4444' : '#10b981' },
                { label: 'Critical θ_cr',      val: fmt(R.shields_cr, 3),      unit: '(Shields 1936)', col: '#94a3b8' },
              ].map(k => (
                <div key={k.label} className="rb-section px-3 py-2">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</div>
                  <div className="text-base font-bold font-mono" style={{ color: k.col }}>{k.val}</div>
                  <div className="text-[10px] text-slate-600">{k.unit}</div>
                </div>
              ))}
            </div>

            {/* Risk indicator */}
            <div className={`p-3 rounded-xl border mb-3 ${
              R.disturbRatio > 1.5 ? 'bg-red-500/8 border-red-500/30' :
              R.disturbRatio > 1.0 ? 'bg-amber-500/8 border-amber-500/30' :
              R.disturbRatio > 0.5 ? 'bg-yellow-500/8 border-yellow-500/30' :
              'bg-emerald-500/5 border-emerald-500/20'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Resuspension Risk</span>
                <span className={`text-sm font-bold ${R.resuspColor}`}>
                  {R.resuspRisk} — θ/θ_cr = {fmt(R.disturbRatio, 2)}
                </span>
              </div>
              <div className="flex gap-2 text-[10px] text-slate-400 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">Negligible &lt;0.5×</span>
                <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">Low 0.5–1.0×</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Moderate 1.0–1.5×</span>
                <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">High &gt;1.5×</span>
              </div>
            </div>

            {/* Effect on bubble size and SOD */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/20">
                <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-2">Bubble Size Effect</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Clean formation</span>
                    <span className="font-mono text-violet-300">{fmt(R.d_b_mm, 3)} mm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Turbulence corr.</span>
                    <span className="font-mono text-amber-300">+{fmt(R.turbCorr * 100, 1)}%</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-700/50 pt-1 mt-1">
                    <span className="text-slate-300">Disturbed ∅ bed</span>
                    <span className="font-mono text-orange-300">{fmt(R.d_b_mm_dist, 3)} mm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Disturbed ∅ surface</span>
                    <span className="font-mono text-orange-200">{fmt(R.d_b_surf_dist, 3)} mm</span>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-2">SOD Enhancement</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nominal SOD</span>
                    <span className="font-mono text-slate-300">{fmt(d.sodGO2m2d, 2)} g/m²/d</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Resuspension ×</span>
                    <span className="font-mono text-amber-300">{fmt(R.sodResuspFactor, 2)}×</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-700/50 pt-1 mt-1">
                    <span className="text-slate-300">Effective SOD</span>
                    <span className="font-mono text-red-300">{fmt(R.effectiveSOD, 2)} g/m²/d</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Demand (coverage)</span>
                    <span className="font-mono text-red-200">{fmt(R.sodKgO2h, 3)} kgO₂/h</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Reservoir coverage assessment */}
          <Card title="Reservoir Coverage Assessment" icon={Database} iconColor="text-cyan-400">
            <button onClick={() => setShowCoverage(s => !s)}
              className="mb-3 flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300">
              {showCoverage ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showCoverage ? 'Collapse' : 'Expand'}
            </button>
            {showCoverage && (
              <>
                {/* Context warning: full-bed-coverage vs reservoir-destratification */}
                <div className="mb-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[11px]">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-slate-300 leading-relaxed">
                      <strong className="text-amber-300">Two different design questions:</strong>{' '}
                      The <strong>“Modules required”</strong> KPI below answers:{' '}
                      <em>“How many modules are needed so that every m² of reservoir bed lies inside the bubble-plume bed influence area?”</em>{' '}
                      — i.e. <strong>full-floor coverage at fine-bubble scale</strong>, which is the design paradigm for <strong>wastewater treatment tanks</strong>{' '}
                      (typical answer: hundreds–thousands of modules per ha).
                      <br /><br />
                      For <strong>reservoir destratification &amp; SOD management</strong>{' '}
                      the correct paradigm is <strong>plume-induced mixing cells</strong> (Wüest et al. 1992 — mixing radius ≈ 5 × depth).
                      Each module then “covers” ~600–2,000 m² of water column, giving <strong>1–4 modules/ha</strong> (AWWA 2011).
                      <br /><br />
                      <button
                        onClick={() => navigate('/reservoir-layout')}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 mt-1 rounded-lg bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 font-semibold transition-all"
                      >
                        Open Reservoir Layout (correct calculation for Barcombe) →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <NI label="Reservoir Area" unit="ha" value={d.reservoirAreaHa}
                    onChange={v => upd({ reservoirAreaHa: v })} step={0.5} min={0.1}
                    tip="Total surface area of reservoir" />
                  <NI label="Target Coverage" unit="%" value={d.targetCoveragePct}
                    onChange={v => upd({ targetCoveragePct: v })} step={5} min={10} max={100}
                    tip="Percentage of reservoir area to aerate. Typically 60–80%." />
                  <NI label="Nominal SOD" unit="g O₂/m²/d" value={d.sodGO2m2d}
                    onChange={v => upd({ sodGO2m2d: v })} step={0.1} min={0}
                    tip="Base sediment O₂ demand (before disturbance factor). Eutrophic UK reservoir: 0.5–2.0 g/m²/day." />
                  <NI label="Blower Efficiency" value={d.blowerEff}
                    onChange={v => upd({ blowerEff: v })} step={0.01} min={0.5} max={0.9} />
                  <NI label="Safety Margin" unit="%" value={d.safetyPct}
                    onChange={v => upd({ safetyPct: v })} step={1} min={0} max={30} />
                </div>

                {/* Modules per ha breakdown */}
                <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20 mb-3 text-xs space-y-1">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Coverage Geometry</div>
                  <div className="flex justify-between text-slate-400">
                    <span>Frame size</span><span className="font-mono text-slate-300">2.0 × 2.0 m</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Corner offset</span><span className="font-mono text-slate-300">{fmt(R.cornerOffset, 2)} m</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Influence radius / diffuser</span><span className="font-mono text-cyan-300">{fmt(R.r_inf, 3)} m</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Edge coverage beyond frame</span>
                    <span className={`font-mono ${R.edgeCoverM > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {R.edgeCoverM > 0 ? `+${fmt(R.edgeCoverM, 3)} m/side` : 'none (within frame)'}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-400 font-bold border-t border-slate-700/50 pt-1 mt-1">
                    <span className="text-slate-300">Effective module pitch</span>
                    <span className="font-mono text-amber-400">{fmt(R.effectivePitchM, 3)} m</span>
                  </div>
                  <div className="flex justify-between text-slate-400 font-bold">
                    <span className="text-slate-300">Modules per hectare</span>
                    <span className="font-mono text-orange-400">{fmt(R.modulesPerHa, 1)} modules/ha</span>
                  </div>
                </div>

                {/* Results — full-bed-coverage paradigm (wastewater tank style) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  {[
                    { label: 'Modules — full bed coverage', val: String(R.modulesRequired),  unit: 'modules (tank-style)', col: '#06b6d4' },
                    { label: 'Coverage area',      val: fmt(R.coveredAreaM2 / 1e4, 2), unit: 'ha covered',  col: '#10b981' },
                    { label: 'Total airflow',      val: fmt(R.totalAirNm3h, 1),         unit: 'Nm³/h',       col: '#8b5cf6' },
                    { label: 'Total AOTR',         val: fmt(R.totalAOTR, 2),            unit: 'kgO₂/h',     col: '#f59e0b' },
                    { label: 'Blower power',       val: fmt(R.blowerPowerKW, 1),        unit: 'kW',          col: '#ef4444' },
                    { label: 'SAE',                val: fmt(R.sae, 2),                   unit: 'kgO₂/kWh',   col: '#22c55e' },
                  ].map(k => (
                    <div key={k.label} className="rb-section px-3 py-2">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</div>
                      <div className="text-lg font-bold font-mono" style={{ color: k.col }}>{k.val}</div>
                      <div className="text-[10px] text-slate-600">{k.unit}</div>
                    </div>
                  ))}
                </div>

                {/* Reservoir-paradigm comparison */}
                {(() => {
                  // Wüest (1992): r_mix = 5 × H_eff; area/module = π r² × 0.65
                  const rMix = 5 * R.effSubm;
                  const areaPerMod = Math.PI * rMix * rMix * 0.65;
                  const reservoirModulesByCov = Math.max(1, Math.ceil((d.reservoirAreaHa * 1e4 * d.targetCoveragePct / 100) / areaPerMod));
                  const sodKgh = (d.sodGO2m2d * d.reservoirAreaHa * 1e4 / 1000) / 24;
                  const reservoirModulesBySOD = Math.max(1, Math.ceil((sodKgh * 1.3) / Math.max(0.001, R.modAOTR)));
                  const recCount = Math.max(reservoirModulesByCov, reservoirModulesBySOD);
                  const recSpacing = Math.sqrt((d.reservoirAreaHa * 1e4) / recCount);
                  return (
                    <div className="mb-3 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                      <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-2">
                        Reservoir-paradigm answer (Wüest 1992 plume mixing)
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <div className="text-[9px] text-slate-500 uppercase">By coverage</div>
                          <div className="text-lg font-bold font-mono text-emerald-300">{reservoirModulesByCov}</div>
                          <div className="text-[9px] text-slate-600">π·(5H)²·0.65</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-500 uppercase">By SOD demand</div>
                          <div className="text-lg font-bold font-mono text-emerald-300">{reservoirModulesBySOD}</div>
                          <div className="text-[9px] text-slate-600">SOD·1.3 / AOTR</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-500 uppercase">Recommended</div>
                          <div className="text-lg font-bold font-mono text-emerald-400">{recCount}</div>
                          <div className="text-[9px] text-slate-600">max() · {fmt(recCount / d.reservoirAreaHa, 1)}/ha</div>
                        </div>
                        <div>
                          <div className="text-[9px] text-slate-500 uppercase">Spacing</div>
                          <div className="text-lg font-bold font-mono text-emerald-300">{fmt(recSpacing, 0)}</div>
                          <div className="text-[9px] text-slate-600">m (square grid)</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-2">
                        For reservoir destratification &amp; SOD management <strong className="text-emerald-300">use this row</strong>{' '}
                        — far fewer modules cover the water column via plume-induced circulation cells rather than bed-level bubble influence.
                      </div>
                    </div>
                  );
                })()}

                {/* O₂ balance bar — uses disturbed SOD */}
                <div className="space-y-2 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">O₂ Mass Balance (with disturbance factor)</div>
                  {[
                    { label: 'SOD (nominal)',  val: R.sodKgO2h_nom, col: 'bg-orange-500' },
                    { label: 'SOD (disturbed)',val: R.sodKgO2h,     col: 'bg-red-500' },
                    { label: 'AOTR supply',    val: R.totalAOTR,    col: 'bg-emerald-500' },
                  ].map(row => (
                    <div key={row.label} className="flex items-center gap-3">
                      <div className="text-[10px] text-slate-400 w-28 shrink-0">{row.label}</div>
                      <div className="flex-1 h-2.5 bg-rb-bg rounded-full overflow-hidden">
                        <div className={`h-full ${row.col} opacity-70 rounded-full`}
                          style={{ width: `${Math.min(100, (row.val / Math.max(R.sodKgO2h, R.totalAOTR, 0.001)) * 100)}%` }} />
                      </div>
                      <div className="text-[10px] font-mono text-slate-300 w-20 text-right">{fmt(row.val, 3)} kg/h</div>
                    </div>
                  ))}
                  <div className={`text-xs mt-2 px-2 py-1.5 rounded-lg border ${R.oxygenSurplus >= 0 ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300' : 'bg-red-500/8 border-red-500/30 text-red-300'}`}>
                    {R.oxygenSurplus >= 0
                      ? `✓ Surplus: +${fmt(R.oxygenSurplus, 3)} kgO₂/h — meets disturbed SOD over ${fmt(R.coveredAreaM2 / 1e4, 2)} ha`
                      : `✗ Deficit: ${fmt(R.oxygenSurplus, 3)} kgO₂/h — increase modules or reduce SOD via sediment management`}
                  </div>
                </div>

                <div className="mt-3 p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-[11px] text-cyan-200/70">
                  <strong className="text-cyan-300">Sedimentation control:</strong>{' '}
                  {R.modulesRequired} × {R.count}-diffuser modules at {fmt(R.cornerOffset, 2)} m corner offset cover {fmt(R.coveredAreaM2 / 1e4, 2)} ha.
                  Effective pitch is {fmt(R.effectivePitchM, 2)} m ({fmt(R.modulesPerHa, 1)} modules/ha)
                  {R.edgeCoverM > 0.005 ? `, including ${fmt(R.edgeCoverM, 3)} m edge coverage beyond each frame.` : '.'}
                  {' '}Resuspension risk is <strong className={R.resuspColor}>{R.resuspRisk}</strong> — effective SOD = {fmt(R.effectiveSOD, 2)} g/m²/d.
                  {R.oxygenSurplus < 0 && ' Reduce corner offset to increase coverage, or increase diffuser count.'}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
